import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Compass, Flame, PenLine, Sparkles } from "lucide-react";

import heroImage from "@/assets/hero-quibblies.jpg";
import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { Button } from "@/components/ui/button";
import { MembersOnlyGate } from "@/components/members-only-gate";
import { useAuth } from "@/lib/auth-context";
import { fetchCategories, fetchPublishedStories } from "@/lib/queries";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Quibblies — Write, share, belong." },
      {
        name: "description",
        content:
          "Quibblies is an 18+ writing, storytelling and self-expression community where adults publish fiction, personal experiences, reflections, poetry and more.",
      },
      { property: "og:title", content: "Quibblies — Write, share, belong." },
      {
        property: "og:description",
        content:
          "Publish fiction, personal experiences, reflections and poetry in a reviewed adult writing community.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HomePage,
});

function StoryGrid({
  loading,
  stories,
  emptyTitle,
  emptyDescription,
  columns = 3,
}: {
  loading: boolean;
  stories: ReturnType<typeof Array.prototype.slice> | undefined;
  emptyTitle: string;
  emptyDescription: string;
  columns?: 2 | 3;
}) {
  const gridClass =
    columns === 2
      ? "grid gap-6 sm:grid-cols-2"
      : "grid gap-6 sm:grid-cols-2 lg:grid-cols-3";

  if (loading) {
    return (
      <div className={gridClass}>
        {Array.from({ length: columns }).map((_, index) => (
          <StoryCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (!stories || stories.length === 0) {
    return <EmptyState icon={BookOpen} title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className={gridClass}>
      {stories.map((story: any) => (
        <StoryCard key={story.id} story={story} />
      ))}
    </div>
  );
}

function HomePage() {
  const { user } = useAuth();

  const newest = useQuery({
    queryKey: ["stories", "newest"],
    queryFn: () => fetchPublishedStories({ limit: 6, orderBy: "published_at" }),
  });
  const trending = useQuery({
    queryKey: ["stories", "trending"],
    queryFn: () => fetchPublishedStories({ limit: 6, orderBy: "like_count" }),
  });
  const categories = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });

  const featured = trending.data?.[0];

  return (
    <div className="pb-8">
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-hero-glow" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-surface/60 px-3 py-1 text-xs text-muted-foreground backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-primary-glow" aria-hidden="true" />
              An adult writing community
            </span>
            <h1 className="mt-5 font-display text-4xl leading-[1.05] text-foreground sm:text-6xl">
              Your words.
              <br />
              <span className="text-gradient">Your experience.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              Quibblies is a quiet, well-made place for adults to write and read — fiction, personal
              experiences, reflections, diaries and poetry. Every publication is reviewed by our
              team before it reaches readers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="hero" size="lg">
                <Link to={user ? "/write" : "/auth"}>
                  <PenLine className="h-4 w-4" aria-hidden="true" />
                  Start writing
                </Link>
              </Button>
              <Button asChild variant="soft" size="lg">
                <Link to="/discover">
                  <Compass className="h-4 w-4" aria-hidden="true" />
                  Explore stories
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[2rem] border border-border/70 shadow-lift">
            <img
              src={heroImage}
              alt="An open book with light and birds rising from its pages"
              width={1600}
              height={1008}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl space-y-16 px-4 py-16 sm:px-6">
        {!user ? (
          <MembersOnlyGate
            title="Writing is for members"
            description="Quibblies is an 18+ community. Sign in or create an account to read the writing our members publish."
          />
        ) : (
          <>
          <section aria-labelledby="featured-heading">
            <SectionHeading
              title="Featured"
              description="Editor-picked writing from the community"
            />
            {trending.isLoading ? (
              <StoryCardSkeleton featured />
            ) : featured ? (
              <StoryCard story={featured} featured />
            ) : (
              <EmptyState
                icon={Sparkles}
                title="No featured story yet"
                description="Once stories are approved by moderation, the best of them appear here."
              />
            )}
          </section>

          <section aria-labelledby="trending-heading">
            <SectionHeading
              title="Trending"
              description="Gaining readers this week"
              href="/discover"
            />
            <StoryGrid
              loading={trending.isLoading}
              stories={trending.data?.slice(1)}
              emptyTitle="Nothing trending yet"
              emptyDescription="Engagement builds as readers like and bookmark new work."
            />
          </section>

          <section aria-labelledby="new-heading">
            <SectionHeading
              title="New publications"
              description="Freshly approved and published"
              href="/discover"
            />
            <StoryGrid
              loading={newest.isLoading}
              stories={newest.data}
              emptyTitle="Nothing published here yet"
              emptyDescription="Be the first to publish — write a draft and send it for review."
            />
          </section>

          </>
        )}

        <section aria-labelledby="categories-heading">
          <SectionHeading title="Popular categories" href="/categories" linkLabel="All categories" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(categories.data ?? []).slice(0, 8).map((category) => (
              <Link
                key={category.id}
                to="/discover"
                search={{ category: category.slug }}
                className="group rounded-2xl border border-border/70 bg-gradient-card p-5 transition-smooth hover:-translate-y-1 hover:border-primary/50 hover:shadow-lift"
              >
                <span className="font-display text-lg text-foreground">{category.name}</span>
                <p className="mt-1 text-sm text-muted-foreground">{category.description}</p>
              </Link>
            ))}
          </div>
        </section>

        {!user ? (
          <>
            <section aria-labelledby="how-heading">
              <SectionHeading
                title="How Quibblies works"
                description="Three separate reviews — people, pictures and prose"
              />
              <div className="grid gap-5 md:grid-cols-3">
                {[
                  {
                    step: "Account",
                    body: "Registered → Email verified → Pending → Approved. Approval means you may take part in the community.",
                  },
                  {
                    step: "Profile media",
                    body: "Uploaded → Pending → Approved or Rejected. Your avatar and cover are reviewed on their own track.",
                  },
                  {
                    step: "Writing",
                    body: "Every publication: Draft → Submitted → Reviewing → Approved, Changes requested or Rejected → Published. Nothing publishes automatically.",
                  },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="rounded-2xl border border-border/70 bg-gradient-card p-6"
                  >
                    <p className="text-xs uppercase tracking-widest text-primary-glow">
                      {item.step}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-muted-foreground">
                Visitors can browse published previews, categories and author pages. Reading full
                publications, following authors, commenting and building a library are for approved
                members.{" "}
                <Link to="/about" className="text-primary-glow underline-offset-4 hover:underline">
                  More about our approach
                </Link>
                .
              </p>
            </section>

            <section className="overflow-hidden rounded-[2rem] border border-border/70 bg-gradient-card p-8 text-center sm:p-12">
              <Flame className="mx-auto h-6 w-6 text-primary-glow" aria-hidden="true" />
              <h2 className="mt-4 font-display text-3xl text-foreground">
                Join a community of writers
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                Create a free account to follow authors, build a library and publish your own
                writing — whatever form it takes. Quibblies is strictly 18+.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild variant="hero" size="lg">
                  <Link to="/auth" search={{ mode: "register" }}>
                    Create your account
                  </Link>
                </Button>
                <Button asChild variant="soft" size="lg">
                  <Link to="/guidelines">Read the guidelines</Link>
                </Button>
              </div>
            </section>
          </>
        ) : null}

      </div>
    </div>
  );
}
